import {
  Component,
  Injector,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DvFilterAreaModule } from '@devinco/ui/legacy';
import { OnCallGridComponent } from './components/on-call-grid/on-call-grid.component';
import { OnCallMapComponent } from './components/on-call-map/on-call-map.component';
import { RegistryComponentNames } from '@shared/constants/registry-component-name';
import { createCustomElement } from '@angular/elements';
import { DriverPopupContentComponent } from '@shared/components/driver-popup-content/driver-popup-content.component';
import { LocationPopupContentComponent } from './components/location-popup-content/location-popup-content.component';
import { Observable } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { OnCallFiltersService } from './services/on-call-filters.service';
import { OnCallFilter, OnCallFilterDto } from './interfaces';
import { LocalStorageItems } from '@shared/constants/local-storage-item';
import { PipesModule } from '@shared/pipes/pipes.module';
import { DirectivesModule } from '@shared/directives/directives.module';
import {
  DvSplitterResizableSide,
  DvVerticalSplitterComponent,
} from '@devinco/ui/dds/splitter';
import { EntityFilterService } from '@shared/services/filter-area/entity-filters.service';
import { Entity } from '@shared/constants/entity';

@Component({
  imports: [
    OnCallGridComponent,
    OnCallMapComponent,
    DirectivesModule,
    AsyncPipe,
    DvFilterAreaModule,
    TranslateModule,
    PipesModule,
    DvVerticalSplitterComponent,
  ],
  selector: 'app-on-call-page',
  templateUrl: './on-call-page.component.html',
  styleUrls: ['../../styles/page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [EntityFilterService<OnCallFilterDto>],
})
export class OnCallPageComponent implements OnInit {
  private entityFiltersService = inject(EntityFilterService<OnCallFilterDto>);
  private onCallFiltersService = inject(OnCallFiltersService);
  resizableSide: typeof DvSplitterResizableSide = DvSplitterResizableSide;
  @ViewChild('grid')
  onCallGrid: OnCallGridComponent;
  isFilterAreaOpen = true;
  availableFilters$: Observable<OnCallFilter[]>;

  constructor() {
    this.entityFiltersService.filterFields =
      this.onCallFiltersService.filterFields;
    this.entityFiltersService.saveFilterDataGetterFn =
      this.onCallFiltersService.saveFilterDataGetterFn;

    const injector = inject(Injector);

    if (!customElements.get(RegistryComponentNames.DriverPopupContent)) {
      const VtDriverMapPopupElement = createCustomElement(
        DriverPopupContentComponent,
        {
          injector,
        }
      );
      customElements.define(
        RegistryComponentNames.DriverPopupContent,
        VtDriverMapPopupElement
      );
    }

    if (!customElements.get(RegistryComponentNames.LocationPopupContent)) {
      const VtMapLocationPopupElement = createCustomElement(
        LocationPopupContentComponent,
        {
          injector,
        }
      );
      customElements.define(
        RegistryComponentNames.LocationPopupContent,
        VtMapLocationPopupElement
      );
    }
  }

  ngOnInit(): void {
    this.getFilters();
    this.toggleFilterAreaVisibility();
  }

  onFilterAreaToggled(isOpen: boolean): void {
    localStorage.setItem(
      LocalStorageItems.OnCallFilterAreaToggleKey,
      String(isOpen)
    );
    this.isFilterAreaOpen = isOpen;
  }

  onActiveFilterChanged(dataFilterId: number): void {
    const selectedFilterData =
      this.entityFiltersService.getSelectedFilterData(dataFilterId);
    if (selectedFilterData) {
      this.onCallGrid.activeFilterChangedCallback(selectedFilterData);
      this.entityFiltersService.setActiveFilter(dataFilterId);
    }
  }

  deselectActiveFilter(): void {
    this.entityFiltersService.deselectActiveFilters();
  }

  addFilterFn = (): void => {
    this.entityFiltersService.openAddNewFilterDialog(
      Entity.OnCallFilter,
      this.onCallGrid.mapPanelFormDataForUpsert()
    );
  };

  onMapRefreshTriggered(): void {
    this.onCallGrid.gridContext.loadClientSideData?.();
  }

  private toggleFilterAreaVisibility(): void {
    const filterAreaStateValue = localStorage.getItem(
      LocalStorageItems.OnCallFilterAreaToggleKey
    );
    this.isFilterAreaOpen =
      filterAreaStateValue === 'true' || filterAreaStateValue === null;
  }

  private getFilters(): void {
    this.entityFiltersService.updateFilterDataGetterFn =
      this.updateFilterDataGetterFn;
    this.entityFiltersService.fetchFilters(Entity.OnCallFilter);
    this.availableFilters$ = this.entityFiltersService.availableFilters$;
  }

  private updateFilterDataGetterFn = (): OnCallFilterDto | undefined => {
    const activeFilterData = this.entityFiltersService.getActiveFilter();

    if (!activeFilterData) return;

    return {
      DataFilterID: activeFilterData.DataFilterID,
      Name: activeFilterData.Name,
      VisibleToAllUsers: activeFilterData.VisibleToAllUsers,
      ...this.onCallGrid.mapPanelFormDataForUpsert(),
    };
  };
}
